import {
  OrderTransportingOrderAvailableDriversItem,
  OrderTransportingOrderAvailableDriversItemStartingAddress,
  OrderTransportingOrderAvailableDriversItemPlan,
  OrderTransportingOrderAvailableDriversItemTaxi,
} from "../../../../../common/services/order/transporting-order/available-drivers/order-transporting-order-available-drivers";
import orderTranslationsHelper from "../../../../../languages/order-translations.helper";
import orderDetailsManageDriverAssignmentHelper from "./order-details-manage-driver-assignment.helper";
import {
  OrderDetailsManageDriverAssignmentDriverAddress,
  OrderDetailsManageDriverAssignmentDriverPlanEntry,
  OrderDetailsManageDriverAssignmentDriverTaxiCorporation,
} from "./types/order-details-manage-driver-assignment-driver";
import OrderDetailsManageDriverAssignmentDriverSelectOption from "./types/order-details-manage-driver-assignment-driver-select-option";
import OrderDetailsManageDriverAssignmentSelectOption from "./types/order-details-manage-driver-assignment-select-option";
import OrderDetailsManageDriverAssignmentTaxiCorporationSelectOption from "./types/order-details-manage-driver-assignment-taxi-corporation-select-option";

const createPlanEntry = (
  responsePlanEntry: OrderTransportingOrderAvailableDriversItemPlan,
): OrderDetailsManageDriverAssignmentDriverPlanEntry => {
  return {
    uuid: responsePlanEntry.id,
    ordersCargoCompanyInternalIds: responsePlanEntry.cargoOrders
      .map((order) => order.humanId)
      .flat(),
  };
};

const createDriverTaxiCorporation = (
  responseTaxiAssociation: OrderTransportingOrderAvailableDriversItemTaxi,
): OrderDetailsManageDriverAssignmentDriverTaxiCorporation => {
  return {
    name: responseTaxiAssociation.companyName,
    displayName: responseTaxiAssociation.displayName,
    uuid: responseTaxiAssociation.id,
  };
};

const createStartingAddress = (
  address: OrderTransportingOrderAvailableDriversItemStartingAddress,
): OrderDetailsManageDriverAssignmentDriverAddress => {
  return {
    lat: address.lat,
    lon: address.lon,
  };
};

const createDriverSelectOption = (
  responseDriver: OrderTransportingOrderAvailableDriversItem,
): OrderDetailsManageDriverAssignmentDriverSelectOption => {
  const planEntries = responseDriver.plans.map(createPlanEntry);
  const taxiCorporations = responseDriver.taxis.map(
    createDriverTaxiCorporation,
  );
  const startingAddress = createStartingAddress(responseDriver.startingAddress);

  const distance = orderDetailsManageDriverAssignmentHelper.getDistanceLabel(
    responseDriver.distance,
  );

  const phoneNumber = `${responseDriver.mobilePrimaryPrefix}${responseDriver.mobilePrimary}`;

  const fleetPartnerList: string[] = [];
  responseDriver.taxis.forEach((taxi) => {
    if (taxi.fleetPartner) {
      fleetPartnerList.push(taxi.fleetPartner);
    }
  });

  const fleetPartners = `${fleetPartnerList.length > 0 ? `, ${fleetPartnerList.join(", ")}` : ""}`;

  return {
    label: `${responseDriver.firstName} ${responseDriver.lastName}`,
    subText: `${distance}, ${responseDriver.startingAddress.fullAddress}, ${phoneNumber}${fleetPartners}`,
    value: {
      uuid: responseDriver.id,
      firstName: responseDriver.firstName,
      lastName: responseDriver.lastName,
      planEntries,
      taxiCorporations,
      startingAddress,
    },
  };
};

const createDriverSelectOptions = (
  responseDrivers: OrderTransportingOrderAvailableDriversItem[],
): OrderDetailsManageDriverAssignmentDriverSelectOption[] => {
  return responseDrivers.map(createDriverSelectOption);
};

const createNewPlanEntryOption =
  (): OrderDetailsManageDriverAssignmentSelectOption => {
    const translations =
      orderTranslationsHelper.getDetailsTranslations().manageDriverAssignment;

    return {
      label: translations.form.assignmentToNewPlanEntrySelectOptionLabel,
      value: null,
    };
  };

const createAssignmentOptionFromPlanEntry = (
  planEntry: OrderDetailsManageDriverAssignmentDriverPlanEntry,
): OrderDetailsManageDriverAssignmentSelectOption => {
  const translations =
    orderTranslationsHelper.getDetailsTranslations().manageDriverAssignment;

  const label =
    translations.form.assignmentToExistingPlanEntrySelectOptionLabelTemplate.replace(
      "#{ordersCargoCompanyInternalIds}",
      planEntry.ordersCargoCompanyInternalIds.join(),
    );
  return {
    label,
    value: {
      planEntry: {
        uuid: planEntry.uuid,
        orderCargoCompanyInternalIds: planEntry.ordersCargoCompanyInternalIds,
      },
    },
  };
};

const createAssignmentOptionsFromPlanEntries = (
  planEntries: OrderDetailsManageDriverAssignmentDriverPlanEntry[],
): OrderDetailsManageDriverAssignmentSelectOption[] => {
  return planEntries.map(createAssignmentOptionFromPlanEntry);
};

const createAssignmentSelectOptions = (
  planEntries: OrderDetailsManageDriverAssignmentDriverPlanEntry[],
): OrderDetailsManageDriverAssignmentSelectOption[] => {
  const newPlanEntryOption = createNewPlanEntryOption();
  const optionsFromPlanEntries =
    createAssignmentOptionsFromPlanEntries(planEntries);

  return [newPlanEntryOption, ...optionsFromPlanEntries];
};

const createTaxiCorporationSelectOptions = (
  driverTaxiAssociations: OrderDetailsManageDriverAssignmentDriverTaxiCorporation[],
): OrderDetailsManageDriverAssignmentTaxiCorporationSelectOption[] => {
  return driverTaxiAssociations.map((taxiCorporation) => {
    return {
      label: taxiCorporation.displayName,
      subText: taxiCorporation.name,
      value: {
        name: taxiCorporation.name,
        uuid: taxiCorporation.uuid,
      },
    };
  });
};

const orderDetailsManageDriverAssignmentFactory = {
  createDriverSelectOptions,
  createAssignmentSelectOptions,
  createTaxiCorporationSelectOptions,
};

export default orderDetailsManageDriverAssignmentFactory;
